<template>
  <div>
    <PageAllow controller="StockDetails" action="index" />
    <Breadcrumbs :items="items" />
    <v-slide-x-transition>
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <!-- 入库日期 / 时间类型 -->
              <SearcherDatetimeRange
                type="datetime"
                v-model="datetimeRage"
                :label="$t('StockDetails.Fields.created_date')"
                removeIcon
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-tooltip
                v-if="$permission.permissionAllow('StockDetails', 'export')"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    v-bind="attrs"
                    v-on="on"
                    dark
                    color="print"
                    class="px-2 me-2 mb-1"
                    @click.stop="exportExcel()"
                  >
                    <v-icon x-small class="pe-1">fa-file-excel</v-icon>
                    {{ $t("StockDetails.export") }}
                  </v-btn>
                </template>
                <span>{{ $t("StockDetails.export") }}</span>
              </v-tooltip>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <!-- 批次号 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="stock_batch_code"
                    v-model="options.stock_batch_code"
                    @text="batchCodeKeyword = $event"
                    :label="$t('StockDetails.Fields.stock_batch_code')"
                  />
                </v-col>
                <!-- 商品分类 -->
                <!-- 商品分类 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherMaterialCategory v-model="categorySelected" />
                </v-col>
                <!-- 商品 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="keyword"
                    v-model="options.keyword"
                    @text="keyword = $event"
                    :label="$t('stockDetail.keyword')"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="stock_batch_code"
                    v-model="options.stock_order_code"
                    @text="orderCodeKeyword = $event"
                    :label="$t('StockDetails.Fields.stock_order_code')"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <!-- 冻结 -->
                  <SearcherStatus
                    :label="$t('StockDetails.prohibit_flg.ObjectName')"
                    :items="$consts.prohibitFlgs"
                    v-model="options.prohibit_flgs"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <!-- 使用状态 -->
                  <SearcherStatus
                    :items="$consts.use.use_status"
                    v-model="options.status"
                    :label="$t('StockDetails.use_status.ObjectName')"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <!-- 表格 -->
        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="stock_detail"
            :options.sync="options"
            :loading="loading"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            multi-sort
            class="elevation-1 px-4 pt-1 sticky-table"
            id="sticky-table"
            :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
            fixed-header
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.actions="{ header }">
              <span class="ml-3">
                {{ header.text }}
                <v-icon x-small>fa-sliders-h</v-icon>
              </span>
            </template>
            <!-- 批次号 -->
            <template v-slot:item.stock_batch_code="{ item }">
              <span class="text-no-wrap caption font-weight-bold">
                {{ item.stock_batch_code }}
              </span>
              <br />
              <span
                class="text-no-wrap caption secondary--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
              >
                {{ item.created | formatTime }}
              </span>
            </template>
            <template v-slot:item.material_name="{ item }">
              <span class="text-no-wrap">
                {{item.material_name, }}
              </span>
              <br />
              <span v-if="item.standard_name" class="caption secondary--text">
                {{ $t("Material.Fields.standard") }}{{ $t("Global.colon")
                }}<span
                  v-if="item.standard_name && item.standard_name.length > 12"
                  :title="item.standard_name"
                  style="cursor: pointer"
                >
                  {{ item.standard_name.slice(0, 12) + "......" }}
                </span>
                <span v-else>
                  {{ item.standard_name }}
                </span>
              </span>
            </template>
            <!-- 状态文字/颜色 -->
            <!-- 冻结状态 -->
            <template v-slot:item.prohibit_flg="{ item, index }">
              <span :v-resize="!index ? $stickTableResizer() : null"> </span>
              <v-chip
                small
                label
                :color="item.prohibit_flg | prohibitFlgsColor"
                :text-color="item.prohibit_flg | prohibitFlgsTextColor"
              >
                {{ item.prohibit_flg | prohibitFlgsText }}
              </v-chip>
            </template>
            <!-- 过期状态 expired_flg-->
            <!-- <template v-slot:item.expired_flg="{ item }">
              <v-chip
                small
                label
                :color="item.expired_flg | expiredFlgsColor"
                :text-color="item.expired_flg | expiredFlgsTextColor"
              >
                {{ item.expired_flg | expiredFlgsText }}
              </v-chip>
            </template> -->
            <template v-slot:item.production_date="{ item }">
              <span>{{ item.production_date | formatDate }}</span>
            </template>
            <template v-slot:item.expired_date="{ item }">
              <span>{{ item.expired_date | formatDate }}</span>
            </template>
            <!-- 入库量格式化 -->
            <template v-slot:item.stock_first="{ item }">
              {{ item.stock_first }}
            </template>
            <!-- 当前库存格式化 -->
            <template v-slot:item.stock_current="{ item }">
              {{ item.stock_current }}
            </template>
            <!-- 格式化金额 -->
            <template v-slot:item.price="{ item }">
              {{ $t("Global.money") }}{{ item.price | formatMoney }}
            </template>
            <!-- 小计 -->
            <template v-slot:item.count_num="{ item }">
              {{ $t("Global.money")
              }}{{ (item.price * item.stock_current) | formatMoney }}
            </template>
            <!-- 操作 -->
            <template v-slot:item.actions="{ item }">
              <StockDetailsActions
                :stock_detail="item"
                showView
                showProhibit
                showUnProhibit
                showLoss
                @done="getStockDetailsFromAPI"
                class="ml-3"
              />
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import i18n from "@/i18n";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import StockDetails from "@/api/stock_details";
import SearcherMaterialCategory from "@/components/plugins/SearcherMaterialCategory";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import StockDetailsActions from "./plugins/Actions";

const defaultOptions = {
  in_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  in_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  category_code: "",
  stock_batch_code: "",
  keyword: "",
  prohibit_flgs: [0, 1],
  status: [0, 2],
};

export default {
  name: "stockDetails",

  data: () => ({
    datetimeRage: [
      new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
        .toISOString()
        .substr(0, 10) + " 00:00:00",
      new Date().toISOString().substr(0, 10) + " 23:59:59",
    ],
    stock_detail: [],
    batchCodeKeyword: "",
    orderCodeKeyword: "",
    materialCategoriesSelected: null,
    keyword: "",
    paging: {},
    options: JSON.parse(JSON.stringify(defaultOptions)),
    stock_first_total: 0,
    stock_current_total: 0,
    price_total: 0,
    categorySelected: null,
  }),

  components: {
    Breadcrumbs,
    SearcherDatetimeRange,
    PageAllow,
    SearcherMaterialCategory,
    SearcherKeyword,
    SearcherStatus,
    StockDetailsActions,
  },
  props: {
    detailMode: Boolean,
  },
  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.stockManagement"),
        disabled: false,
        href: "/current-stocks",
      },
      {
        text: i18n.t("Nav.stockDetails"),
        disabled: true,
        href: "/stock-details",
      },
    ],
    headers() {
      var headers = [
        {
          text: i18n.t("StockDetails.Fields.stock_batch_code"),
          align: "start",
          sortable: false,
          value: "stock_batch_code",
        },
        {
          text: i18n.t("StockDetails.Fields.material_code"),
          align: "start",
          sortable: false,
          value: "material_code",
        },
        {
          text: i18n.t("StockDetails.Fields.material"),
          align: "start",
          sortable: false,
          value: "material_name",
        },
        {
          text: i18n.t("StockDetails.Fields.material_category"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "category_name",
        },
        {
          text: i18n.t("StockDetails.Fields.unit"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "unit",
        },
        {
          text: i18n.t("StockDetails.Fields.price"),
          align: "end",
          sortable: false,
          value: "price",
        },
        // 入库量
        {
          text: i18n.t("StockDetails.Fields.stock_first"),
          align: "end",
          sortable: false,
          value: "stock_first",
        },
        // 当前库存
        {
          text: i18n.t("StockDetails.Fields.stock_current"),
          align: "end",
          sortable: false,
          value: "stock_current",
        },
        // 小计
        {
          text: i18n.t("StockDetails.Fields.count_num"),
          align: "end",
          sortable: false,
          value: "count_num",
        },
      ];
      if (
        this.accountSettings &&
        this.accountSettings.product_date_setting == 1
      ) {
        headers.push(
          {
            text: i18n.t("stockInOrders.DetailFields.production_date"),
            align: "start",
            value: "production_date",
            width: 120,
          },
          {
            text: i18n.t("stockInOrders.DetailFields.expired_date"),
            align: "start",
            value: "expired_date",
            width: 120,
          }
        );
      }
      // 冻结状态
      headers.push(
        {
          text: i18n.t("StockDetails.Fields.prohibit_flg"),
          align: "start",
          sortable: false,
          value: "prohibit_flg",
        },
        // 操作
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        }
      );
      return headers;
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "stickTableCols", "accountSettings"]),
  },

  mounted() {
    this.$commonUtils
      .loadOptions("stock_detail", [
        "materialCategoriesSelected",
        "categorySelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
            if (
              typeof this.options.in_time_from != "undefined" &&
              typeof this.options.in_time_to != "undefined"
            )
              this.datetimeRage = [
                this.options.in_time_from,
                this.options.in_time_to,
              ];
          } else this.resetSearch();
          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "materialCategoriesSelected":
                this.materialCategoriesSelected = selected.value;
                break;
              case "categorySelected":
                this.categorySelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },
  methods: {
    onSearch() {
      // 搜索
      this.options = Object.assign({}, this.options, {
        stock_batch_code: this.batchCodeKeyword,
        stock_order_code: this.orderCodeKeyword,
        keyword: this.keyword,
      });
    },
    resetSearch() {
      this.datetimeRage = [
        new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
          .toISOString()
          .substr(0, 10) + " 00:00:00",
        new Date().toISOString().substr(0, 10) + " 23:59:59",
      ];

      this.materialCategoriesSelected = null;
      this.categorySelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      //重置搜索框
      let clearArr = [
        this.$refs.keyword,
      ];
      clearArr.forEach((k) => {
        if (k) k.clear();
      });
    },
    getStockDetailsFromAPI() {
      this.id = this.$route.query.stock_id;
      StockDetails.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stock_detail = response.data.stock_details;
            this.stock_first_total = response.data.stock_first_total;
            this.stock_current_total = response.data.stock_current_total;
            this.price_total = response.data.price_total;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    exportExcel() {
      StockDetails.exports(this.options);
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.getStockDetailsFromAPI();
        sessionStorage.setItem(
          "stock_detail-options",
          JSON.stringify(this.options)
        );
      },
    },

    // 仓库组别

    // 商品分类
    categorySelected: {
      handler() {
        this.options.category_code = this.categorySelected
          ? this.categorySelected.code
          : "";
        sessionStorage.setItem(
          "stock_detail-categorySelected",
          JSON.stringify(this.categorySelected)
        );
      },
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.in_time_from =
            new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.in_time_from = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.in_time_to =
            new Date().toISOString().substr(0, 10) + " 23:59:59";
        } else {
          this.options.in_time_to = this.datetimeRage[1];
        }
      },
    },
  },
};
</script>

<style>
</style>